import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import PageTitle from "../../components/Common/PageTitle";
import {
  getAllInvoiceDetailSelector
} from "../../store/invoiceSlice";
import { getCompanyData } from "../../store/companySlice";
import {
  defaultInputSmStyle,
} from "../../constants/defaultStyles";
import gstService from "../../services/gstService";
import userService from "../../services/userService";

function GSTScreen() {
  const allInvoiceDetails = useSelector(getAllInvoiceDetailSelector);
  const companyData = useSelector(getCompanyData);

  // Authentication state - using Supabase auth state
  const [authState, setAuthState] = useState({ user: null, session: null, loading: true });

  // Date range state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1); // Default to last month
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });

  // Selected invoices for GST filing
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filingType, setFilingType] = useState('GSTR1'); // 'GSTR1', 'GSTR3B', 'GSTR9', 'GSTR9C'
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(() => {
    // Default to current financial year
    const availableYears = gstService.getAvailableFinancialYears();
    return availableYears[0]?.value || new Date().getFullYear().toString();
  });
  const [annualSummary, setAnnualSummary] = useState(null);
  const [filingResult, setFilingResult] = useState(null);
  const [evcOtp, setEvcOtp] = useState('');
  const [isRequestingEvc, setIsRequestingEvc] = useState(false);

  // Step 2: summary + history helpers
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [filingHistory, setFilingHistory] = useState([]);
  const [summaryPreview, setSummaryPreview] = useState(null);
  const [resultTab, setResultTab] = useState('filing'); // 'filing' | 'summary' | 'history'

  // GST Portal Credentials State (Perione API — two-step OTP)
  const [gstCredentials, setGstCredentials] = useState({
    username: '',    // GST portal user name
    authOtp: '',     // OTP received on portal-registered mobile (step 2)
    pan: '',         // PAN of authorised signatory (needed for EVC OTP)
    rememberMe: false
  });

  // 'enter_username' -> 'enter_otp' -> 'authenticated'
  const [perioneAuthStep, setPerioneAuthStep] = useState('enter_username');
  const [perioneAuth, setPerioneAuth] = useState({
    authToken: '',
    tokenExpiry: null,
    isAuthenticated: false,
  });
  const [isRequestingAuthOtp, setIsRequestingAuthOtp] = useState(false);
  const [isVerifyingAuthOtp, setIsVerifyingAuthOtp] = useState(false);

  const handleRequestAuthOtp = useCallback(async () => {
    if (!companyData?.companyGST) {
      toast.error('Set company GSTIN before logging in to Perione');
      return;
    }
    if (!gstCredentials.username) {
      toast.error('GST portal username is required');
      return;
    }
    try {
      setIsRequestingAuthOtp(true);
      await gstService.perioneRequestAuthOtp({
        username: gstCredentials.username,
        gstin: companyData.companyGST,
      });
      setPerioneAuthStep('enter_otp');
      toast.success('OTP sent to the GSTIN-registered mobile number');
    } catch (e) {
      console.error('Perione OTP request failed:', e);
      toast.error(`Perione OTP request failed: ${e.message}`);
    } finally {
      setIsRequestingAuthOtp(false);
    }
  }, [companyData?.companyGST, gstCredentials.username]);

  const handleVerifyAuthOtp = useCallback(async () => {
    if (!companyData?.companyGST) {
      toast.error('Set company GSTIN before logging in to Perione');
      return;
    }
    if (!gstCredentials.username || !gstCredentials.authOtp) {
      toast.error('Username and OTP are required');
      return;
    }
    try {
      setIsVerifyingAuthOtp(true);
      const result = await gstService.perioneVerifyAuthOtp({
        username: gstCredentials.username,
        gstin: companyData.companyGST,
        otp: gstCredentials.authOtp,
      });
      setPerioneAuth({
        authToken: result.authToken,
        tokenExpiry: result.expiryTime,
        isAuthenticated: true,
      });
      setPerioneAuthStep('authenticated');
      toast.success('Perione authenticated successfully');
    } catch (e) {
      console.error('Perione verify OTP failed:', e);
      toast.error(`Perione verify OTP failed: ${e.message}`);
    } finally {
      setIsVerifyingAuthOtp(false);
    }
  }, [companyData?.companyGST, gstCredentials.username, gstCredentials.authOtp]);

  // Check authentication status
  // Listen to Supabase auth state changes
  // Check authentication status
  // Uses userService which manages the application's authentication state
  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        // Use userService as the source of truth for authentication
        const currentUser = userService.getCurrentUser();

        // Validate the session to ensure it's still active
        // This is more robust than separate Supabase auth state as it aligns with App.js
        const isValid = await userService.validateSession();

        if (mounted) {
          if (isValid && currentUser) {
            setAuthState({
              user: currentUser,
              session: {
                access_token: userService.sessionToken,
                user: currentUser
              },
              loading: false
            });
          } else {
            // If validation fails, user might be logged out in another tab
            setAuthState({ user: null, session: null, loading: false });
          }
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        if (mounted) {
          setAuthState({ user: null, session: null, loading: false });
        }
      }
    };

    checkAuth();

    // Periodically re-validate session to handle expiration or logout in other tabs
    const intervalId = setInterval(checkAuth, 60000); // Check every minute

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  // Auto-surface filing result in the results panel
  useEffect(() => {
    if (filingResult) setResultTab('filing');
  }, [filingResult]);

  // Calculate annual summary when filing type or financial year changes
  useEffect(() => {
    if ((filingType === 'GSTR9' || filingType === 'GSTR9C') && allInvoiceDetails.length > 0) {
      const summary = gstService.calculateAnnualSummary(allInvoiceDetails, selectedFinancialYear);
      setAnnualSummary(summary);
      
      // Auto-select all invoices for the financial year
      const fyStart = new Date(`${selectedFinancialYear}-04-01`);
      const fyEnd = new Date(`${parseInt(selectedFinancialYear) + 1}-03-31`);
      
      const annualInvoiceIds = new Set(
        allInvoiceDetails
          .filter(invoice => {
            const invoiceDate = new Date(invoice.date);
            return invoiceDate >= fyStart && invoiceDate <= fyEnd;
          })
          .map(invoice => invoice.id)
      );
      
      setSelectedInvoices(annualInvoiceIds);
      setSelectAll(annualInvoiceIds.size === allInvoiceDetails.length);
    } else {
      setAnnualSummary(null);
    }
  }, [filingType, selectedFinancialYear, allInvoiceDetails]);

  // Filter states
  const [showOnlyPaid, setShowOnlyPaid] = useState(false);
  const [showOnlyGSTCollected, setShowOnlyGSTCollected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // Filter invoices by date range and additional filters
  const filteredInvoices = useMemo(() => {
    if (!startDate || !endDate) return allInvoiceDetails;

    return allInvoiceDetails.filter(invoice => {
      const invoiceDate = new Date(invoice.createdDate);
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Set time to start/end of day for proper comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // Check if invoice is within date range
      const isInRange = invoiceDate >= start && invoiceDate <= end;
      
      // Apply additional filters
      if (showOnlyPaid && invoice.statusName !== 'Paid') return false;
      if (showOnlyGSTCollected && (!invoice.taxes || invoice.taxes.length === 0 || 
        invoice.taxes.reduce((sum, tax) => sum + (parseFloat(tax.amount) || 0), 0) <= 0)) return false;
      
      return isInRange;
    });
  }, [allInvoiceDetails, startDate, endDate, showOnlyPaid, showOnlyGSTCollected]);

  // Paginated invoices
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredInvoices.slice(startIndex, endIndex);
  }, [filteredInvoices, currentPage, pageSize]);

  // Calculate totals for selected invoices
  const selectedInvoiceTotals = useMemo(() => {
    const selectedInvoiceData = filteredInvoices.filter(inv => selectedInvoices.has(inv.id));
    
    return selectedInvoiceData.reduce((totals, invoice) => {
      const invoiceAmount = parseFloat(invoice.totalAmount) || 0;
      // Calculate individual tax components using only the proven approach from gstTransformer.js
      let cgst = 0, sgst = 0, igst = 0;
      
      // Extract from taxes array using title field (same as gstTransformer.js)
      if (invoice.taxes && Array.isArray(invoice.taxes)) {
        invoice.taxes.forEach(tax => {
          const title = (tax.title || '').toLowerCase();
          const amount = parseFloat(tax.amount) || 0;

          if (title.includes('cgst')) {
            cgst += amount;
          } else if (title.includes('sgst')) {
            sgst += amount;
          } else if (title.includes('igst')) {
            igst += amount;
          }
        });
      }
      
      // Calculate total tax amount from individual components only
      const taxAmount = cgst + sgst + igst;
      
      return {
        count: totals.count + 1,
        totalAmount: totals.totalAmount + invoiceAmount,
        totalTaxAmount: totals.totalTaxAmount + taxAmount,
        totalCGST: totals.totalCGST + cgst,
        totalSGST: totals.totalSGST + sgst,
        totalIGST: totals.totalIGST + igst,
      };
    }, {
      count: 0,
      totalAmount: 0,
      totalTaxAmount: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
    });
  }, [filteredInvoices, selectedInvoices]);

  // Calculate totals for filtered invoices
  const filteredTotals = useMemo(() => {
    return filteredInvoices.reduce((totals, invoice) => {
      const amount = parseFloat(invoice.totalAmount) || 0;
      const isPaid = invoice.paymentStatus === 'PAID' || invoice.statusName === 'Paid';
      
      return {
        totalCollected: totals.totalCollected + (isPaid ? amount : 0),
        totalDue: totals.totalDue + (!isPaid ? amount : 0),
      };
    }, {
      totalCollected: 0,
      totalDue: 0,
    });
  }, [filteredInvoices]);

  // Generate a downloadable summary (CSV — opens in Excel) of selected invoices
  // for reconciliation before filing.
  const handleGenerateSummary = useCallback(() => {
    if (selectedInvoices.size === 0) {
      toast.error('Select invoices in the table above first');
      return;
    }
    try {
      setIsGeneratingSummary(true);
      const selectedData = filteredInvoices.filter(inv => selectedInvoices.has(inv.id));
      const gstin = companyData?.companyGST || '';
      const companyName = companyData?.companyName || '';

      const escape = (v) => {
        const s = String(v ?? '').replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };

      const rows = [];
      rows.push(['GSTR Filing Summary']);
      rows.push(['Generated', new Date().toLocaleString()]);
      rows.push(['GSTIN', gstin]);
      rows.push(['Company', companyName]);
      rows.push(['Filing Type', filingType]);
      rows.push(['Period', `${startDate} to ${endDate}`]);
      rows.push(['Total Invoices', selectedData.length]);
      rows.push([]);
      rows.push([
        'Invoice No', 'Client', 'Date', 'Status',
        'Taxable Amount', 'CGST', 'SGST', 'IGST', 'Total Tax', 'Total Amount',
      ]);

      let tCGST = 0, tSGST = 0, tIGST = 0, tAmt = 0, tTax = 0;
      selectedData.forEach(inv => {
        let cgst = 0, sgst = 0, igst = 0;
        if (Array.isArray(inv.taxes)) {
          inv.taxes.forEach(t => {
            const title = (t.title || '').toLowerCase();
            const amt = parseFloat(t.amount) || 0;
            if (title.includes('cgst')) cgst += amt;
            else if (title.includes('sgst')) sgst += amt;
            else if (title.includes('igst')) igst += amt;
          });
        }
        const tax = cgst + sgst + igst;
        const total = parseFloat(inv.totalAmount) || 0;
        const taxable = total - tax;
        tCGST += cgst; tSGST += sgst; tIGST += igst; tAmt += total; tTax += tax;
        rows.push([
          inv.invoiceNumber || inv.invoiceNo || inv.invoice_number || `INV-${inv.id}`,
          inv.clientDetail?.name || '',
          inv.createdDate ? new Date(inv.createdDate).toLocaleDateString() : '',
          inv.statusName || '',
          taxable.toFixed(2),
          cgst.toFixed(2),
          sgst.toFixed(2),
          igst.toFixed(2),
          tax.toFixed(2),
          total.toFixed(2),
        ]);
      });
      rows.push([]);
      rows.push([
        'TOTALS', '', '', '',
        (tAmt - tTax).toFixed(2),
        tCGST.toFixed(2),
        tSGST.toFixed(2),
        tIGST.toFixed(2),
        tTax.toFixed(2),
        tAmt.toFixed(2),
      ]);

      const csv = rows.map(r => r.map(escape).join(',')).join('\r\n');
      // UTF-8 BOM so Excel picks up encoding (especially for ₹)
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });

      const safeGstin = gstin || 'NOGSTIN';
      const filename = `GST_Summary_${filingType}_${safeGstin}_${startDate}_to_${endDate}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSummaryPreview({
        count: selectedData.length,
        totalTaxable: tAmt - tTax,
        totalCGST: tCGST,
        totalSGST: tSGST,
        totalIGST: tIGST,
        totalTax: tTax,
        totalAmount: tAmt,
        filename,
      });
      setResultTab('summary');
      toast.success(`Summary downloaded: ${filename}`);
    } catch (e) {
      console.error('Summary generation failed:', e);
      toast.error(`Failed to generate summary: ${e.message}`);
    } finally {
      setIsGeneratingSummary(false);
    }
  }, [
    companyData?.companyGST,
    companyData?.companyName,
    endDate,
    filingType,
    filteredInvoices,
    selectedInvoices,
    startDate,
  ]);

  // Load filing history:
  //  1. Local records from this app's localforage (shows app-initiated filings).
  //  2. Live status from Perione for the last 6 return periods
  //     (so the user sees filings done outside this app too).
  const handleCheckFilingHistory = useCallback(async () => {
    try {
      setIsFetchingHistory(true);

      // --- Local history ---
      const localP = gstService.getFilingHistory().catch(() => []);

      // --- Live Perione statuses for last 6 months ---
      const gstin = companyData?.companyGST;
      const canGoRemote = perioneAuth.isAuthenticated && !!gstin;
      let livePromise = Promise.resolve([]);
      if (canGoRemote) {
        const periods = [];
        const now = new Date();
        for (let i = 1; i <= 6; i++) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yyyy = String(d.getFullYear());
          periods.push(`${mm}${yyyy}`);
        }
        livePromise = Promise.all(
          periods.map(p =>
            gstService.perioneFilingStatus({ gstin, retPeriod: p })
              .then(res => ({ retPeriod: p, ok: true, data: res?.data ?? res }))
              .catch(err => ({ retPeriod: p, ok: false, error: err?.message || 'Failed' }))
          )
        );
      }

      const [localHistory, liveResults] = await Promise.all([localP, livePromise]);

      // Normalize Perione responses into flat records
      const extractReturns = (d) => {
        if (!d || typeof d !== 'object') return [];
        if (d.rtntype || d.returnType || d.return_typ || d.rtn_typ) return [d];
        const arrayKeys = ['returns', 'data', 'list', 'records', 'filings', 'retList'];
        for (const k of arrayKeys) {
          const v = d[k];
          if (Array.isArray(v)) return v;
          if (v && typeof v === 'object') {
            for (const k2 of arrayKeys) {
              if (Array.isArray(v[k2])) return v[k2];
            }
            if (v.rtntype || v.returnType || v.return_typ || v.rtn_typ) return [v];
          }
        }
        return [];
      };

      const liveRecords = [];
      for (const r of liveResults) {
        if (!r.ok) continue;
        const rets = extractReturns(r.data);
        if (rets.length === 0) {
          // Period came back empty / no filings — surface as an "unfiled" marker
          liveRecords.push({
            type: '—',
            returnPeriod: r.retPeriod,
            status: 'not filed',
            source: 'perione',
          });
          continue;
        }
        rets.forEach(entry => {
          liveRecords.push({
            type: entry.rtntype || entry.returnType || entry.return_typ || entry.rtn_typ || 'GST',
            returnPeriod: r.retPeriod,
            status: String(entry.status || entry.sts || entry.mof || 'unknown').toLowerCase(),
            arn: entry.arn || entry.ARN || entry.arnNumber,
            filedAt: entry.dof || entry.filedDate || entry.filedAt || entry.doa,
            invoiceCount: entry.invcount ?? entry.invoiceCount,
            source: 'perione',
          });
        });
      }

      const taggedLocal = (Array.isArray(localHistory) ? localHistory : []).map(h => ({
        ...h,
        source: 'local',
      }));

      const combined = [...liveRecords, ...taggedLocal];
      setFilingHistory(combined);
      setResultTab('history');

      if (combined.length === 0) {
        if (!canGoRemote) {
          toast.info('Log in with Perione (Step 1) to fetch live filing status');
        } else {
          toast.info('No filings found for this GSTIN in the last 6 periods');
        }
      } else {
        const failed = liveResults.filter(r => !r.ok).length;
        toast.success(
          `Loaded ${combined.length} filing record(s)` +
          (failed ? ` (${failed} period(s) failed to fetch)` : '')
        );
      }
    } catch (e) {
      console.error('Failed to load filing history:', e);
      toast.error(`Failed to load filing history: ${e.message}`);
    } finally {
      setIsFetchingHistory(false);
    }
  }, [companyData?.companyGST, perioneAuth.isAuthenticated]);

  // Enhanced validation helper with detailed feedback
  const validateFilingInputs = useCallback(() => {
    // Validate company GSTIN
    if (!companyData?.companyGST) {
      toast.error('Company GSTIN missing. Please update your company profile first.');
      return false;
    }

    // Perione must be authenticated before filing.
    if (!perioneAuth.isAuthenticated) {
      toast.error('Please complete Perione authentication before filing');
      return false;
    }
    if (!gstCredentials.username) {
      toast.error('GST portal username is required');
      return false;
    }

    // Validate EVC OTP format
    if (!evcOtp || evcOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit EVC OTP');

      // Show guidance for getting EVC OTP
      setTimeout(() => {
        toast.info('💡 Click "Get EVC OTP" button to request OTP using your GST credentials', {
          autoClose: 6000,
          position: 'bottom-right'
        });
      }, 1000);
      return false;
    }

    // Validate invoice selection
    if (selectedInvoices.size === 0) {
      toast.error('Please select at least one invoice to file');
      return false;
    }

    // Validate date range
    if (!startDate || !endDate) {
      toast.error('Please select a valid date range for filing');
      return false;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) {
      toast.error('Start date cannot be after end date');
      return false;
    }

    // Additional validation for large date ranges
    const daysDiff = (end - start) / (1000 * 60 * 60 * 24);
    if (daysDiff > 90) {
      toast.warning('Filing period exceeds 90 days. Consider splitting into smaller periods for better processing.');
    }

    return true;
  }, [companyData?.companyGST, evcOtp, selectedInvoices.size, startDate, endDate, gstCredentials.username, perioneAuth.isAuthenticated]);

  // Refs for the 6 EVC OTP inputs so we can auto-advance / auto-retreat focus.
  const otpInputsRef = useRef([]);
  const setOtpInputRef = useCallback((el, index) => {
    otpInputsRef.current[index] = el;
  }, []);

  // Handle typing into a single OTP box. Digits-only; auto-advances to the next
  // box on entry, and supports pasting a full 6-digit OTP into any box.
  const handleEvcOtpChange = useCallback((index, rawValue) => {
    const digits = String(rawValue || '').replace(/\D/g, '');

    setEvcOtp(prev => {
      const arr = prev.split('');
      while (arr.length < 6) arr.push('');

      if (digits.length === 0) {
        arr[index] = '';
      } else {
        // Spread digits across consecutive boxes starting from `index`.
        for (let i = 0; i < digits.length && index + i < 6; i++) {
          arr[index + i] = digits[i];
        }
      }
      return arr.join('').substring(0, 6);
    });

    if (digits.length > 0) {
      const nextIndex = Math.min(index + digits.length, 5);
      // Defer so focus moves after React commits the value update.
      requestAnimationFrame(() => {
        const el = otpInputsRef.current[nextIndex];
        if (el) {
          el.focus();
          if (typeof el.select === 'function') el.select();
        }
      });
    }
  }, []);

  // Backspace on an empty box → move focus back. Arrow keys navigate between boxes.
  const handleEvcOtpKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace') {
      if (!evcOtp[index] && index > 0) {
        e.preventDefault();
        const prevEl = otpInputsRef.current[index - 1];
        if (prevEl) {
          prevEl.focus();
          if (typeof prevEl.select === 'function') prevEl.select();
        }
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      otpInputsRef.current[index + 1]?.focus();
    }
  }, [evcOtp]);

  // Paste full OTP anywhere — distribute into boxes starting from the target.
  const handleEvcOtpPaste = useCallback((index, e) => {
    const text = e.clipboardData?.getData('text') || '';
    const digits = text.replace(/\D/g, '').slice(0, 6 - index);
    if (!digits) return;
    e.preventDefault();
    handleEvcOtpChange(index, digits);
  }, [handleEvcOtpChange]);

  // Update selectAll state when filtered invoices or selected invoices change
  useEffect(() => {
    if (filteredInvoices.length === 0) {
      setSelectAll(false);
    } else {
      const allFilteredSelected = filteredInvoices.every(invoice => selectedInvoices.has(invoice.id));
      setSelectAll(allFilteredSelected && selectedInvoices.size > 0);
    }
  }, [filteredInvoices, selectedInvoices]);

  // Handle EVC OTP request (for filing). Perione now needs PAN + form type,
  // not the GST portal password.
  const handleEvcOtpRequest = useCallback(async () => {
    if (!perioneAuth.isAuthenticated) {
      toast.error('Please complete Perione authentication first.');
      return;
    }

    if (!gstCredentials.pan) {
      toast.error('PAN of authorised signatory is required for EVC OTP.');
      return;
    }

    const formType = filingType === 'GSTR3B' ? '3B'
      : filingType === 'GSTR9' ? 'R9'
      : filingType === 'GSTR9C' ? 'R9C'
      : 'R1';

    setIsRequestingEvc(true);

    try {
      const result = await gstService.perioneRequestEvcOtp({
        gstin: companyData?.companyGST,
        pan: gstCredentials.pan,
        formType,
      });

      if (result.success) {
        toast.success('EVC OTP sent successfully! Check your registered mobile.');
      } else {
        toast.error(`Failed to send EVC OTP: ${result.error}`);
      }
    } catch (error) {
      console.error('EVC OTP request error:', error);
      toast.error(`Failed to request EVC OTP: ${error.message}`);
    } finally {
      setIsRequestingEvc(false);
    }
  }, [
    gstCredentials.pan,
    perioneAuth.isAuthenticated,
    companyData?.companyGST,
    filingType,
  ]);

  // Handle select all invoices
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedInvoices(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filteredInvoices.map(inv => inv.id));
      setSelectedInvoices(allIds);
      setSelectAll(true);
    }
  }, [selectAll, filteredInvoices]);

  // Handle individual invoice selection
  const handleInvoiceSelect = useCallback((invoiceId) => {
    setSelectedInvoices(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(invoiceId)) {
        newSelection.delete(invoiceId);
      } else {
        newSelection.add(invoiceId);
      }
      
      // Update select all state - check if all filtered invoices are selected
      setSelectAll(newSelection.size === filteredInvoices.length && filteredInvoices.length > 0);
      
      return newSelection;
    });
  }, [filteredInvoices.length]);

  // Pagination helpers
  const goToPrevPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);



  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Enhanced automated GST filing with comprehensive error handling and annual returns support
  const handleFileGST = useCallback(async () => {
    // Validate inputs with enhanced feedback
    if (!validateFilingInputs()) {
      return;
    }

    const companyGSTIN = companyData?.companyGST;
    setIsProcessing(true);
    setFilingResult(null);

    // Determine if this is annual returns filing
    const isAnnualReturn = filingType === 'GSTR9' || filingType === 'GSTR9C';

    // Progress notification
    toast.info(`🚀 Starting automated ${filingType} filing process...`, {
      autoClose: 3000
    });

    try {
      let result;

      if (isAnnualReturn) {
        // Handle annual returns filing
        const selectedInvoiceData = filteredInvoices.filter(inv =>
          selectedInvoices.has(inv.id)
        );

        if (filingType === 'GSTR9') {
          // Calculate annual summary for GSTR-9
          const summary = gstService.calculateAnnualSummary(selectedInvoiceData, selectedFinancialYear);
          setAnnualSummary(summary);

          // First save the GSTR-9 data
          const saveResult = await gstService.saveGSTR9(companyGSTIN, selectedFinancialYear, {
            gstin: companyGSTIN,
            financialYear: selectedFinancialYear,
            totalTurnover: summary.totalTurnover,
            transportTurnover: summary.transportTurnover,
            courierTurnover: summary.courierTurnover,
            totalTaxPaid: summary.totalTaxPaid,
            totalITCAvailed: summary.totalITCAvailed
          });

          // Then file it with OTP
          if (saveResult.success) {
            result = await gstService.fileGSTR9(companyGSTIN, selectedFinancialYear, evcOtp, companyData?.companyPAN);
          } else {
            result = saveResult;
          }

        } else if (filingType === 'GSTR9C') {
          // Handle GSTR-9C with audit data
          const summary = gstService.calculateAnnualSummary(selectedInvoiceData, selectedFinancialYear);
          
          if (!summary.requiresAudit) {
            toast.warning('GSTR-9C is only required for companies with turnover > ₹2 crores. Consider filing GSTR-9 instead.');
            setIsProcessing(false);
            return;
          }

          setAnnualSummary(summary);

          // For GSTR-9C, we need additional audit data
          const auditData = {
            auditedTurnover: summary.totalTurnover,
            booksTurnover: summary.totalTurnover, // Assuming books match for demo
            auditedTax: {
              iamt: Math.floor(summary.totalTaxPaid * 0.6),
              camt: Math.floor(summary.totalTaxPaid * 0.2),
              samt: Math.floor(summary.totalTaxPaid * 0.2)
            },
            booksTax: {
              iamt: Math.floor(summary.totalTaxPaid * 0.6),
              camt: Math.floor(summary.totalTaxPaid * 0.2),
              samt: Math.floor(summary.totalTaxPaid * 0.2)
            },
            auditedITC: {
              iamt: Math.floor(summary.totalITCAvailed * 0.6),
              camt: Math.floor(summary.totalITCAvailed * 0.2),
              samt: Math.floor(summary.totalITCAvailed * 0.2)
            },
            booksITC: {
              iamt: Math.floor(summary.totalITCAvailed * 0.6),
              camt: Math.floor(summary.totalITCAvailed * 0.2),
              samt: Math.floor(summary.totalITCAvailed * 0.2)
            },
            auditorName: "CA Transport Auditor",
            auditorPan: "ABCDE1234F",
            auditDate: new Date().toLocaleDateString('en-GB'),
            auditPlace: "Delhi",
            transportTurnover: summary.transportTurnover,
            courierTurnover: summary.courierTurnover
          };

          // Save GSTR-9C
          const saveResult = await gstService.saveGSTR9C(companyGSTIN, selectedFinancialYear, auditData);

          // Then file it
          if (saveResult.success) {
            result = await gstService.fileGSTR9C(companyGSTIN, selectedFinancialYear, evcOtp, companyData?.companyPAN);
          } else {
            result = saveResult;
          }
        }
      } else {
        // Handle monthly returns (GSTR-1, GSTR-3B)
        const selectedInvoiceData = filteredInvoices.filter(inv =>
          selectedInvoices.has(inv.id)
        );

        result = await gstService.automatedFiling(
          filingType,
          selectedInvoiceData,
          companyGSTIN,
          startDate,
          endDate,
          evcOtp
        );
      }

      if (result.success) {
        setFilingResult(result);

        // Enhanced success notification
        const successMsg = `🎉 ${result.filingType || filingType} filed successfully!`;
        const detailMsg = isAnnualReturn 
          ? `ARN: ${result.arn || 'Pending'} | Financial Year: ${selectedFinancialYear}`
          : `ARN: ${result.arn || 'Pending'} | ${result.invoiceCount || selectedInvoices.size} invoice(s) processed`;

        toast.success(`${successMsg}\n${detailMsg}`, {
          autoClose: 12000
        });

        // Show automation details
        if (result.automation) {
          setTimeout(() => {
            const automationFeatures = [];
            if (result.automation.evcUsed) automationFeatures.push('✅ EVC automated');
            if (result.automation.sessionManaged) automationFeatures.push('✅ Session managed');
            if (result.automation.validationPassed) automationFeatures.push('✅ Validation passed');

            toast.info(`🤖 Automation: ${automationFeatures.join(', ')}`, {
              autoClose: 6000,
              position: 'bottom-right'
            });
          }, 2000);
        }

        // Show filing efficiency info
        if (result.rateHint === 'reused_session') {
          toast.info('⚡ Efficient filing - existing session reused!', {
            autoClose: 5000,
            position: 'bottom-left'
          });
        } else if (result.rateHint === 'new_session_auto') {
          toast.info('🔄 New session created automatically for filing', {
            autoClose: 5000,
            position: 'bottom-left'
          });
        }

        // Clear selections and reset form
        setSelectedInvoices(new Set());
        setSelectAll(false);
        setEvcOtp('');

      } else {
        const errorMsg = result.error || 'Filing failed';
        console.error('GST Filing Error:', result);

        // Enhanced error handling with specific guidance
        if (errorMsg.includes('session')) {
          toast.error(`🔐 Session Error: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Session will be auto-recreated on next attempt', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('EVC') || errorMsg.includes('OTP')) {
          toast.error(`🔢 EVC OTP Error: ${errorMsg}`, { autoClose: 8000 });
          setEvcOtp(''); // Clear invalid OTP
          toast.info('💡 Please request a new EVC OTP and try again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('rate') || errorMsg.includes('limit')) {
          toast.error(`⚠️ Rate Limit: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Please wait a few minutes before trying again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else if (errorMsg.includes('validation')) {
          toast.error(`📋 Validation Error: ${errorMsg}`, { autoClose: 8000 });
          toast.info('💡 Please check your invoice data and try again', {
            autoClose: 5000,
            position: 'bottom-right'
          });
        } else {
          toast.error(`❌ Filing Error: ${errorMsg}`, { autoClose: 8000 });
        }
      }

    } catch (error) {
      console.error("GST filing error:", error);

      // Comprehensive error handling with user guidance
      const errorMessage = error.message || "Failed to file GST. Please try again.";

      if (errorMessage.includes('Unauthorized') || errorMessage.includes('not authenticated')) {
        toast.error('🔐 Authentication expired. Please refresh and try again.', {
          autoClose: 8000
        });
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error('🌐 Network error. Please check your connection and try again.', {
          autoClose: 8000
        });
        toast.info('💡 Check your internet connection and retry', {
          autoClose: 5000,
          position: 'bottom-right'
        });
      } else if (errorMessage.includes('Invalid GSTIN')) {
        toast.error('📝 Invalid company GSTIN. Please update your company profile.', {
          autoClose: 8000
        });
      } else if (errorMessage.includes('No invoices')) {
        toast.error('📋 No invoices selected. Please select invoices to file.', {
          autoClose: 6000
        });
      } else {
        toast.error(`❌ Unexpected error: ${errorMessage}`, {
          autoClose: 8000
        });

        // Offer general troubleshooting
        setTimeout(() => {
          toast.info('💡 Try refreshing the page or contact support if issue persists', {
            autoClose: 8000,
            position: 'bottom-right'
          });
        }, 2000);
      }
    } finally {
      setIsProcessing(false);
    }
  }, [selectedInvoices, filteredInvoices, companyData, startDate, endDate, filingType, selectedFinancialYear, evcOtp, validateFilingInputs]);

  // Handle date range preset
  const handleDateRangePreset = useCallback((preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisQuarter':
        const quarter = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), quarter * 3, 1);
        end = today;
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        if (lastQuarter < 0) {
          start = new Date(today.getFullYear() - 1, 9, 1); // Q4 of previous year
          end = new Date(today.getFullYear() - 1, 11, 31);
        } else {
          start = new Date(today.getFullYear(), lastQuarter * 3, 1);
          end = new Date(today.getFullYear(), (lastQuarter + 1) * 3, 0);
        }
        break;
      default:
        return;
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  }, []);

  // Show loading while checking authentication
  if (authState.loading) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-xl p-6">
          <PageTitle title="GST Filing" />
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading GST services...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-white rounded-xl p-6">
        <PageTitle title="GST Filing" />

        {/* Date Range Filter */}
        <div className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Date Filtering Active
            </div>
          </div>

          <div className="bg-white border border-blue-200 rounded-lg p-4">
            {/* Date Range Presets */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleDateRangePreset('thisMonth')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                This Month
              </button>
              <button
                onClick={() => handleDateRangePreset('lastMonth')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                Last Month
              </button>
              <button
                onClick={() => handleDateRangePreset('thisQuarter')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                This Quarter
              </button>
              <button
                onClick={() => handleDateRangePreset('lastQuarter')}
                className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-md transition-colors"
              >
                Last Quarter
              </button>
            </div>

            {/* Custom Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={defaultInputSmStyle}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className={defaultInputSmStyle}
                />
              </div>
            </div>

            {/* Filtered Totals Preview */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-green-800">Total Collected</h4>
                <div className="text-2xl font-bold text-green-900">
                  <NumberFormat
                    value={filteredTotals.totalCollected}
                    displayType="text"
                    thousandSeparator
                    prefix="₹"
                  />
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-yellow-800">Total Due</h4>
                <div className="text-2xl font-bold text-yellow-900">
                  <NumberFormat
                    value={filteredTotals.totalDue}
                    displayType="text"
                    thousandSeparator
                    prefix="₹"
                  />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800">Total Invoices</h4>
                <div className="text-2xl font-bold text-blue-900">
                  {filteredInvoices.length}
                </div>
              </div>
            </div>

            {/* Invoice Filters */}
            <div className="mt-6 border-t border-blue-100 pt-4 pb-4">
              <div className="flex items-center gap-6">
                <label className="flex items-center text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyPaid}
                    onChange={(e) => setShowOnlyPaid(e.target.checked)}
                    className="rounded border-gray-300 mr-2"
                  />
                  <span className="text-gray-700">Show only Paid</span>
                </label>
                <label className="flex items-center text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyGSTCollected}
                    onChange={(e) => setShowOnlyGSTCollected(e.target.checked)}
                    className="rounded border-gray-300 mr-2"
                  />
                  <span className="text-gray-700">Show only GST Collected</span>
                </label>
              </div>
            </div>

            {/* Invoice Table */}
            <div className="-mx-4 -mb-6">
              <table className="w-full bg-white border border-gray-200">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left py-3 px-4">Invoice No</th>
                    <th className="text-left py-3 px-4">Client</th>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Tax Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice) => {
                    const taxAmount = invoice.taxes?.reduce((sum, tax) =>
                      sum + (parseFloat(tax.amount || tax.value) || 0), 0
                    ) || parseFloat(invoice.totalTaxAmount || invoice.tax_amount || invoice.taxAmount) || 
                    (parseFloat(invoice.cgst || 0) + parseFloat(invoice.sgst || 0) + parseFloat(invoice.igst || 0)) || 0;

                    return (
                      <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={selectedInvoices.has(invoice.id)}
                            onChange={() => handleInvoiceSelect(invoice.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="py-3 px-4">
                          {invoice.invoiceNumber || invoice.invoiceNo || invoice.invoice_number || invoice.number || `INV-${invoice.id}` || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          {invoice.clientDetail?.name || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          {new Date(invoice.createdDate).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            invoice.statusName === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : invoice.statusName === 'Unpaid'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.statusName || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <NumberFormat
                            value={invoice.totalAmount || 0}
                            displayType="text"
                            thousandSeparator
                            prefix="₹"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <NumberFormat
                            value={taxAmount}
                            displayType="text"
                            thousandSeparator
                            prefix="₹"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors whitespace-nowrap"
                  >
                    {selectAll ? 'Deselect All' : 'Select All'}
                  </button>

                  <label className="flex items-center text-sm text-gray-700 gap-2">
                    Rows
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[5, 10, 25, 50].map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-600">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, filteredInvoices.length)} to {Math.min(currentPage * pageSize, filteredInvoices.length)} of {filteredInvoices.length} invoices
                  </div>
                  <button
                    onClick={goToPrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {Math.ceil(filteredInvoices.length / pageSize)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredInvoices.length / pageSize), prev + 1))}
                    disabled={currentPage >= Math.ceil(filteredInvoices.length / pageSize)}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Selected Invoices Summary */}
              {selectedInvoices.size > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mx-4 mt-6 mb-6">
                  <h4 className="text-lg font-semibold text-indigo-900 mb-3">
                    Selected for GST Filing ({selectedInvoiceTotals.count} invoices)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-indigo-700">Total Amount:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalAmount}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">Total Tax:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalTaxAmount}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">CGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalCGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">SGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalSGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="text-indigo-700">IGST:</span>
                      <div className="font-bold text-indigo-900">
                        <NumberFormat
                          value={selectedInvoiceTotals.totalIGST}
                          displayType="text"
                          thousandSeparator
                          prefix="₹"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Smart GST Filing — guided 4-step flow */}
        <div className="mb-6 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
          <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900">Smart GST Filing</h3>
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Guided 4-Step Flow
              </div>
            </div>

            {/* Filing For */}
            <div className="bg-white border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-blue-600 text-sm font-medium">Filing For:</span>
                <span className="text-gray-900 font-semibold">
                  {companyData?.companyGST || companyData?.GST || 'GST Number not set'}
                </span>
                <span className="text-sm text-gray-600">
                  ({companyData?.companyName || 'Company Name'})
                </span>
              </div>
            </div>

            {/* ───────── Step 1: Perione Authentication ───────── */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className={`flex items-center gap-3 px-4 py-3 border-b ${perioneAuth.isAuthenticated ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full font-semibold text-sm ${perioneAuth.isAuthenticated ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}>
                  {perioneAuth.isAuthenticated ? '✓' : '1'}
                </div>
                <h4 className="font-semibold text-gray-900">Authenticate with Perione</h4>
                {perioneAuth.isAuthenticated && (
                  <span className="ml-auto text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    Authenticated
                  </span>
                )}
              </div>

              <div className="p-4">
                {!perioneAuth.isAuthenticated ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GST Portal Username
                      </label>
                      <input
                        type="text"
                        value={gstCredentials.username}
                        onChange={(e) => setGstCredentials(prev => ({ ...prev, username: e.target.value }))}
                        disabled={perioneAuthStep === 'enter_otp'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        placeholder="Enter GST portal username"
                      />
                    </div>

                    {perioneAuthStep === 'enter_username' && (
                      <>
                        <p className="text-xs text-gray-600">
                          Perione will send an OTP to the mobile number registered on the GST portal for this GSTIN.
                        </p>
                        <button
                          onClick={handleRequestAuthOtp}
                          disabled={
                            isRequestingAuthOtp ||
                            !gstCredentials.username ||
                            !companyData?.companyGST
                          }
                          className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        >
                          {isRequestingAuthOtp ? (
                            <>
                              <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                              Requesting OTP…
                            </>
                          ) : (
                            <>📲 Request OTP</>
                          )}
                        </button>
                      </>
                    )}

                    {perioneAuthStep === 'enter_otp' && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          OTP (sent to GSTIN-registered mobile)
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={gstCredentials.authOtp}
                            onChange={(e) => setGstCredentials(prev => ({
                              ...prev,
                              authOtp: e.target.value.replace(/\D/g, '').slice(0, 6),
                            }))}
                            className="w-40 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 tracking-widest"
                            placeholder="6-digit OTP"
                          />
                          <button
                            onClick={handleVerifyAuthOtp}
                            disabled={isVerifyingAuthOtp || gstCredentials.authOtp.length !== 6}
                            className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                          >
                            {isVerifyingAuthOtp ? (
                              <>
                                <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                                Verifying…
                              </>
                            ) : (
                              <>✅ Verify & Login</>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setPerioneAuthStep('enter_username');
                              setGstCredentials(prev => ({ ...prev, authOtp: '' }));
                            }}
                            className="px-3 py-2 text-xs text-purple-700 hover:underline"
                          >
                            Resend OTP
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-green-700 flex-wrap">
                    <span className="font-medium">
                      ✓ Authenticated as <span className="text-gray-900 font-mono">{gstCredentials.username}</span>
                    </span>
                    {perioneAuth.tokenExpiry && (
                      <span className="text-xs text-gray-600">
                        Token expires: {new Date(perioneAuth.tokenExpiry).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ───────── Step 2: Verify Summary & History ───────── */}
            <div className={`bg-white border rounded-lg overflow-hidden ${perioneAuth.isAuthenticated ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full font-semibold text-sm ${perioneAuth.isAuthenticated ? 'bg-blue-500 text-white' : 'bg-gray-400 text-white'}`}>2</div>
                <h4 className="font-semibold text-gray-900">Verify Summary &amp; Past Filings</h4>
                <span className="ml-auto text-xs text-gray-500 hidden md:inline">Optional but recommended</span>
              </div>

              <div className="p-4 space-y-3">
                <p className="text-xs text-gray-600">
                  Download a spreadsheet of the selected invoices to reconcile before filing, and review past filings and their status.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleGenerateSummary}
                    disabled={!perioneAuth.isAuthenticated || selectedInvoices.size === 0 || isGeneratingSummary}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                    title={
                      !perioneAuth.isAuthenticated
                        ? 'Complete Step 1 first'
                        : selectedInvoices.size === 0
                        ? 'Select invoices in the table above'
                        : 'Download summary spreadsheet (CSV opens in Excel)'
                    }
                  >
                    {isGeneratingSummary ? (
                      <>
                        <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                        Generating…
                      </>
                    ) : (
                      <>📊 Generate Summary (XL)</>
                    )}
                  </button>

                  <button
                    onClick={handleCheckFilingHistory}
                    disabled={!perioneAuth.isAuthenticated || isFetchingHistory}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    {isFetchingHistory ? (
                      <>
                        <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                        Loading…
                      </>
                    ) : (
                      <>🗂️ Check Filing History</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ───────── Step 3: EVC Authentication ───────── */}
            <div className={`bg-white border rounded-lg overflow-hidden ${perioneAuth.isAuthenticated ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
              <div className={`flex items-center gap-3 px-4 py-3 border-b ${evcOtp.length === 6 ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`flex items-center justify-center w-7 h-7 rounded-full font-semibold text-sm ${
                  evcOtp.length === 6
                    ? 'bg-green-500 text-white'
                    : perioneAuth.isAuthenticated
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-400 text-white'
                }`}>
                  {evcOtp.length === 6 ? '✓' : '3'}
                </div>
                <h4 className="font-semibold text-gray-900">EVC Authentication</h4>
                {evcOtp.length === 6 && (
                  <span className="ml-auto text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    OTP Ready
                  </span>
                )}
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Authorised Signatory PAN
                    </label>
                    <input
                      type="text"
                      value={gstCredentials.pan}
                      onChange={(e) => setGstCredentials(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                      maxLength={10}
                      disabled={!perioneAuth.isAuthenticated}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 tracking-wider"
                      placeholder="ABCDE1234F"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      PAN is used to request the EVC OTP from the GST portal.
                    </p>
                  </div>
                  <div>
                    {/* invisible spacer so the button aligns with the input on its left */}
                    <span
                      className="block text-sm font-medium mb-1 invisible select-none"
                      aria-hidden="true"
                    >
                      &nbsp;
                    </span>
                    <button
                      onClick={handleEvcOtpRequest}
                      disabled={!perioneAuth.isAuthenticated || isRequestingEvc || !gstCredentials.pan}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                      title={
                        !perioneAuth.isAuthenticated
                          ? 'Complete Step 1 first'
                          : !gstCredentials.pan
                          ? 'Enter authorised signatory PAN'
                          : 'Request EVC OTP'
                      }
                    >
                      {isRequestingEvc ? (
                        <>
                          <div className="animate-spin w-3 h-3 border border-white border-t-transparent rounded-full"></div>
                          Requesting…
                        </>
                      ) : (
                        <>🔐 Generate EVC OTP</>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    EVC OTP (6 digits)
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex gap-1">
                      {[...Array(6)].map((_, index) => (
                        <input
                          key={index}
                          ref={(el) => setOtpInputRef(el, index)}
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={1}
                          value={evcOtp[index] || ''}
                          onChange={(e) => handleEvcOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleEvcOtpKeyDown(index, e)}
                          onPaste={(e) => handleEvcOtpPaste(index, e)}
                          onFocus={(e) => e.target.select()}
                          disabled={!perioneAuth.isAuthenticated}
                          className={`w-10 h-10 text-center border rounded text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            evcOtp[index] ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                          } disabled:bg-gray-100`}
                        />
                      ))}
                    </div>
                    <span className="ml-2 text-sm text-gray-600">{evcOtp.length}/6</span>
                    {evcOtp.length === 6 && (
                      <span className="ml-2 text-sm text-green-600">✓ Ready to file</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ───────── Step 4: Select Filing Type & File ───────── */}
            <div className={`bg-white border rounded-lg overflow-hidden ${perioneAuth.isAuthenticated && evcOtp.length === 6 ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full font-semibold text-sm ${perioneAuth.isAuthenticated && evcOtp.length === 6 ? 'bg-blue-500 text-white' : 'bg-gray-400 text-white'}`}>4</div>
                <h4 className="font-semibold text-gray-900">Select Filing Type &amp; Submit</h4>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filing Type
                    </label>
                    <select
                      value={filingType}
                      onChange={(e) => setFilingType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <optgroup label="Monthly Returns">
                        <option value="GSTR1">GSTR-1</option>
                        <option value="GSTR3B">GSTR-3B</option>
                      </optgroup>
                      <optgroup label="Annual Returns">
                        <option value="GSTR9">GSTR-9 (Annual Return)</option>
                        <option value="GSTR9C">GSTR-9C (Audit Return)</option>
                      </optgroup>
                    </select>
                  </div>

                  {(filingType === 'GSTR9' || filingType === 'GSTR9C') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Financial Year
                      </label>
                      <select
                        value={selectedFinancialYear}
                        onChange={(e) => setSelectedFinancialYear(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {gstService.getAvailableFinancialYears().map(fy => (
                          <option key={fy.value} value={fy.value}>
                            {fy.label} ({fy.period})
                          </option>
                        ))}
                      </select>
                      <div className="text-xs text-gray-500 mt-1">
                        Financial year for annual return filing
                      </div>
                    </div>
                  )}
                </div>

                {(filingType === 'GSTR9' || filingType === 'GSTR9C') && annualSummary && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">
                      📊 Annual Return Summary for FY {annualSummary.financialYear}-{parseInt(annualSummary.financialYear) + 1}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <div className="text-gray-600">Total Invoices</div>
                        <div className="font-medium text-blue-900">{annualSummary.totalInvoices}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Total Turnover</div>
                        <div className="font-medium text-blue-900">₹{(annualSummary.totalTurnover / 100000).toFixed(1)}L</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Tax Paid</div>
                        <div className="font-medium text-blue-900">₹{(annualSummary.totalTaxPaid / 100000).toFixed(1)}L</div>
                      </div>
                      <div>
                        <div className="text-gray-600">ITC Availed</div>
                        <div className="font-medium text-blue-900">₹{(annualSummary.totalITCAvailed / 100000).toFixed(1)}L</div>
                      </div>
                    </div>
                    {filingType === 'GSTR9C' && (
                      <div className="mt-2 text-xs text-blue-700">
                        ⚠️ GSTR-9C is required as annual turnover exceeds ₹2 crores. Audit certificate will be included.
                      </div>
                    )}
                  </div>
                )}

                {/* Big File Button */}
                <button
                  onClick={handleFileGST}
                  disabled={
                    isProcessing ||
                    !perioneAuth.isAuthenticated ||
                    selectedInvoices.size === 0 ||
                    !evcOtp ||
                    evcOtp.length !== 6
                  }
                  className="w-full px-4 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-md font-semibold text-base hover:from-green-700 hover:to-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin w-4 h-4 border border-white border-t-transparent rounded-full"></div>
                      Filing in Progress...
                    </>
                  ) : (
                    <>
                      {filingType === 'GSTR9' || filingType === 'GSTR9C'
                        ? `📋 File ${filingType} for FY ${selectedFinancialYear}-${parseInt(selectedFinancialYear) + 1} (${selectedInvoices.size} invoices)`
                        : `🚀 File ${filingType} Return (${selectedInvoices.size} selected)`}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ───────── Results Panel (below the big file button) ───────── */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center bg-gray-50 border-b border-gray-200">
                <button
                  onClick={() => setResultTab('filing')}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    resultTab === 'filing'
                      ? 'text-blue-700 border-b-2 border-blue-600 bg-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Filing Result
                  {filingResult && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${filingResult.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {filingResult.success ? 'Filed' : 'Failed'}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setResultTab('summary')}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    resultTab === 'summary'
                      ? 'text-blue-700 border-b-2 border-blue-600 bg-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Summary Preview
                  {summaryPreview && (
                    <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                      {summaryPreview.count}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setResultTab('history')}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    resultTab === 'history'
                      ? 'text-blue-700 border-b-2 border-blue-600 bg-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Filing History
                  {filingHistory.length > 0 && (
                    <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                      {filingHistory.length}
                    </span>
                  )}
                </button>
              </div>

              <div className="p-4">
                {resultTab === 'filing' && (
                  filingResult ? (
                    <div className={`p-3 rounded-md border ${filingResult.success
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                      }`}>
                      <div className="font-medium mb-1">
                        {filingResult.success ? '✅ Filing Successful' : '❌ Filing Failed'}
                      </div>
                      <div className="text-sm space-y-1">
                        {filingResult.success ? (
                          <>
                            <div>ARN: {filingResult.arn || 'Pending'} | Status: Filed</div>
                            {(filingResult.returnType === 'GSTR9' || filingResult.returnType === 'GSTR9C') && (
                              <div>
                                Return Type: {filingResult.returnType} |
                                Financial Year: {filingResult.retPeriod || selectedFinancialYear}-{parseInt(filingResult.retPeriod || selectedFinancialYear) + 1}
                              </div>
                            )}
                            {filingResult.returnType === 'GSTR9C' && (
                              <div className="text-xs text-green-700 mt-1">
                                📋 Audit certificate included with annual return filing
                              </div>
                            )}
                          </>
                        ) : (
                          <div>Error: {filingResult.error}</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      No filing submitted yet. Complete the steps above and press the file button.
                    </div>
                  )
                )}

                {resultTab === 'summary' && (
                  summaryPreview ? (
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-gray-600 text-xs">Invoices</div>
                          <div className="font-semibold text-gray-900">{summaryPreview.count}</div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">Taxable</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalTaxable} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">CGST</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalCGST} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">SGST</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalSGST} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">IGST</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalIGST} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">Total Tax</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalTax} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs">Grand Total</div>
                          <div className="font-semibold text-gray-900">
                            <NumberFormat value={summaryPreview.totalAmount} displayType="text" thousandSeparator prefix="₹" decimalScale={2} />
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                        Downloaded: <code className="text-gray-700">{summaryPreview.filename}</code>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      Click "Generate Summary (XL)" in Step&nbsp;2 to export and preview totals here.
                    </div>
                  )
                )}

                {resultTab === 'history' && (
                  filingHistory.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500">
                        Live status pulled from Perione for the last 6 return periods,
                        merged with filings recorded locally on this device.
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 text-left text-gray-600">
                              <th className="py-2 px-3">Source</th>
                              <th className="py-2 px-3">Return</th>
                              <th className="py-2 px-3">Period</th>
                              <th className="py-2 px-3">Status</th>
                              <th className="py-2 px-3">Filed On</th>
                              <th className="py-2 px-3">Invoices</th>
                              <th className="py-2 px-3">ARN / Ref</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filingHistory.map((h, idx) => {
                              const status = (h.status || 'unknown').toLowerCase();
                              const isFiled = status === 'filed' || status === 'submitted';
                              const isFailed = status === 'failed' || status === 'error';
                              const isNotFiled = status === 'not filed';
                              return (
                                <tr key={idx} className="border-b border-gray-100">
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      h.source === 'perione'
                                        ? 'bg-purple-100 text-purple-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {h.source === 'perione' ? 'Perione (live)' : 'Local'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 font-medium text-gray-900">
                                    {h.type || h.returnType || '—'}
                                  </td>
                                  <td className="py-2 px-3 text-gray-700">
                                    {h.returnPeriod || h.retPeriod || '—'}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      isFiled
                                        ? 'bg-green-100 text-green-700'
                                        : isFailed
                                        ? 'bg-red-100 text-red-700'
                                        : isNotFiled
                                        ? 'bg-amber-100 text-amber-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {h.status || 'unknown'}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-gray-700">
                                    {h.filedAt
                                      ? (typeof h.filedAt === 'string' && !/^\d{4}-/.test(h.filedAt)
                                          ? h.filedAt
                                          : new Date(h.filedAt).toLocaleString())
                                      : '—'}
                                  </td>
                                  <td className="py-2 px-3 text-gray-700">
                                    {h.invoiceCount ?? '—'}
                                  </td>
                                  <td className="py-2 px-3 text-xs text-gray-600 font-mono break-all">
                                    {h.arn || h.refId || h.ref_id || '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      {perioneAuth.isAuthenticated
                        ? 'No filing history loaded yet. Click "Check Filing History" in Step 2 — we\'ll fetch live status from Perione for the last 6 periods.'
                        : 'Complete Step 1 (Perione login) first, then click "Check Filing History" in Step 2 to pull live status from the GST portal.'}
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Security Notice */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <span className="text-blue-600">🔒</span>
              <div className="text-blue-700">
                <strong>Security:</strong> Credentials go directly to the GST portal through Perione's secure API.
                We do not store or log your password or OTPs anywhere in our systems.
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default GSTScreen;